import React, { useCallback, useEffect, useState } from 'react';
import { adminGetUsers, adminGetCompanies, adminGetGroups } from '../../api/admin';
import type { SafeUser, Company, StatusGroup } from '../../types/auth';
import { useAuthStore } from '../../store/useAuthStore';
import { useCallStore } from '../../store/useCallStore';
import { useToastStore } from '../../store/useToastStore';
import { FRONTEND_VERSION, fetchBackendVersion } from '../../api/version';
import UsersTab from './UsersTab';
import CompaniesTab from './CompaniesTab';
import GroupsTab from './GroupsTab';
import NotesTab from './NotesTab';
import SessionsTab from './SessionsTab';
import AuditTab from './AuditTab';
import StatsTab from './StatsTab';
import CacheTab from './CacheTab';
import MonitoringTab from './MonitoringTab';

// Eigenständige Admin-Seite (#/admin) — kein Modal mehr: die Anrufhistorie samt
// Auto-Refresh ist hier komplett unmounted und erzeugt keine Last im Hintergrund.

type Section = 'users' | 'companies' | 'groups' | 'notes' | 'sessions' | 'audit' | 'stats' | 'cache' | 'monitoring';

const NAV: { id: Section; label: string; icon: string }[] = [
  { id: 'users', label: 'Benutzer', icon: '👤' },
  { id: 'companies', label: 'Unternehmen', icon: '🏢' },
  { id: 'groups', label: 'Gruppen', icon: '🔗' },
  { id: 'notes', label: 'Notizen & Status', icon: '📝' },
  { id: 'sessions', label: 'Sitzungen', icon: '🔑' },
  { id: 'audit', label: 'Audit-Log', icon: '📋' },
  { id: 'stats', label: 'Statistiken', icon: '📈' },
  { id: 'cache', label: 'Cache', icon: '⚡' },
  { id: 'monitoring', label: 'Monitoring', icon: '📡' },
];

interface Props {
  onLogout: () => void;
  onChangePassword: () => void;
}

export default function AdminPage({ onLogout, onChangePassword }: Props) {
  const addToast = useToastStore((s) => s.addToast);
  const { user, sessionId } = useAuthStore();
  const { darkMode, toggleDarkMode } = useCallStore();
  const [section, setSection] = useState<Section>('users');

  const [users, setUsers] = useState<SafeUser[]>([]);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [groups, setGroups] = useState<StatusGroup[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [backendVersion, setBackendVersion] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const [u, c, g] = await Promise.all([adminGetUsers(), adminGetCompanies(), adminGetGroups()]);
      setUsers(u);
      setCompanies(c);
      setGroups(g);
    } catch {
      addToast('Fehler beim Laden der Admin-Daten.', 'danger');
    } finally {
      setLoaded(true);
    }
  }, [addToast]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    fetchBackendVersion().then((v) => setBackendVersion(v.version)).catch(() => undefined);
  }, []);

  return (
    <div className="min-vh-100 bg-body-tertiary d-flex flex-column">
      <header className="bg-body border-bottom shadow-sm py-2 px-4">
        <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
          <div className="d-flex align-items-center gap-3">
            <span style={{ fontSize: '1.4rem' }} aria-hidden>⚙️</span>
            <div>
              <h1 className="h6 mb-0 fw-bold">Administration</h1>
              <span className="text-muted" style={{ fontSize: '0.72rem' }}>eves_MDR Anrufhistorie</span>
            </div>
          </div>
          <div className="d-flex align-items-center gap-2">
            <a className="btn btn-sm btn-outline-primary" href="#/">← Zur Anrufliste</a>
            <button className="btn btn-sm btn-outline-secondary" onClick={toggleDarkMode} title={darkMode ? 'Heller Modus' : 'Dunkler Modus'}>
              {darkMode ? '☀️' : '🌙'}
            </button>
            <button className="btn btn-sm btn-outline-secondary" onClick={onChangePassword}>Passwort</button>
            <button className="btn btn-sm btn-outline-danger" onClick={onLogout}>Abmelden</button>
          </div>
        </div>
      </header>

      <div className="container-fluid flex-grow-1 py-4 px-4">
        <div className="row g-4">
          <nav className="col-12 col-lg-2">
            <div className="nav flex-lg-column nav-pills gap-1 flex-row flex-wrap">
              {NAV.map((n) => (
                <button
                  key={n.id}
                  className={`nav-link text-start d-flex align-items-center gap-2 py-2 ${section === n.id ? 'active' : ''}`}
                  style={{ fontSize: '0.88rem' }}
                  onClick={() => setSection(n.id)}
                >
                  <span aria-hidden style={{ width: 20, textAlign: 'center' }}>{n.icon}</span>
                  {n.label}
                </button>
              ))}
            </div>
            <div className="text-muted mt-3 px-2 d-none d-lg-block" style={{ fontSize: '0.68rem' }}>
              Angemeldet als {user?.displayName}<br />
              Version {FRONTEND_VERSION}
              {backendVersion && backendVersion !== FRONTEND_VERSION && <> · Backend {backendVersion}</>}
            </div>
          </nav>

          <main className="col-12 col-lg-10">
            <div className="card border-0 shadow-sm rounded-3">
              <div className="card-body">
                {!loaded ? (
                  <div className="text-center py-5"><span className="spinner-border" /></div>
                ) : section === 'users' ? (
                  <UsersTab users={users} companies={companies} onChanged={load} />
                ) : section === 'companies' ? (
                  <CompaniesTab companies={companies} groups={groups} onChanged={load} />
                ) : section === 'groups' ? (
                  <GroupsTab groups={groups} companies={companies} onChanged={load} />
                ) : section === 'notes' ? (
                  <NotesTab companies={companies} groups={groups} />
                ) : section === 'sessions' ? (
                  <SessionsTab currentSessionId={sessionId ?? undefined} />
                ) : section === 'audit' ? (
                  <AuditTab companies={companies} />
                ) : section === 'stats' ? (
                  <StatsTab />
                ) : section === 'monitoring' ? (
                  <MonitoringTab />
                ) : (
                  <CacheTab />
                )}
              </div>
            </div>
          </main>
        </div>
      </div>
    </div>
  );
}
